import { useEffect, useRef, useMemo, useCallback } from "react";

interface Particle {
  // Position when forming the text
  ox: number;
  oy: number;
  // Explosion destination
  ex: number;
  ey: number;
  // Size
  radius: number;
  // Random seed for variation
  speed: number;
}

interface ParticleTextProps {
  /** 0 = particles scattered/invisible, 0.5 = fully assembled as text, 1 = exploded away */
  progress: number;
  width: number;
  height: number;
  className?: string;
  textPart1?: string;
  textPart2?: string;
  /** When true, render part1 on line 1 and part2 on line 2 */
  multiLine?: boolean;
}

// Deterministic random
function seededRandom(seed: number) {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

function sampleTextPixels(
  text: string,
  font: string,
  color: string,
  canvasW: number,
  canvasH: number,
  isPrimary: boolean
): { x: number; y: number; isPrimary: boolean }[] {
  const offscreen = document.createElement("canvas");
  offscreen.width = canvasW;
  offscreen.height = canvasH;
  const ctx = offscreen.getContext("2d")!;
  ctx.clearRect(0, 0, canvasW, canvasH);
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvasW / 2, canvasH / 2);

  const imageData = ctx.getImageData(0, 0, canvasW, canvasH);
  const pixels: { x: number; y: number; isPrimary: boolean }[] = [];

  // Sample every Nth pixel for performance
  const step = Math.max(2, Math.round(canvasW / 350));

  for (let y = 0; y < canvasH; y += step) {
    for (let x = 0; x < canvasW; x += step) {
      const i = (y * canvasW + x) * 4;
      if (imageData.data[i + 3] > 128) {
        pixels.push({ x, y, isPrimary });
      }
    }
  }

  return pixels;
}

const ParticleText = ({ progress, width, height, className, textPart1 = "Você e sua marca ", textPart2 = "no topo.", multiLine = false }: ParticleTextProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const pixelDataRef = useRef<{ isPrimary: boolean }[]>([]);
  const isInitRef = useRef(false);
  const lastDims = useRef({ w: 0, h: 0 });

  const buildParticles = useCallback((w: number, h: number) => {
    if (w < 10 || h < 10) return;

    // Determine font size based on width (matching tailwind text-3xl to text-7xl)
    let fontSize: number;
    if (w >= 1024) fontSize = Math.round(w * 0.058); // ~lg:text-7xl
    else if (w >= 768) fontSize = Math.round(w * 0.06);  // ~md:text-6xl
    else if (w >= 640) fontSize = Math.round(w * 0.055); // ~sm:text-4xl
    else fontSize = Math.round(w * 0.09); // mobile (larger for multiline legibility)

    const font = `800 ${fontSize}px "Space Grotesk", "Inter", sans-serif`;

    const part1 = textPart1;
    const part2 = textPart2;

    const offscreen = document.createElement("canvas");
    offscreen.width = w;
    offscreen.height = h;
    const ctx = offscreen.getContext("2d")!;
    ctx.clearRect(0, 0, w, h);
    ctx.font = font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ffffff";

    if (multiLine) {
      // Two-line rendering: part1 on top, part2 below
      const lineGap = fontSize * 0.3;
      const line1Y = h / 2 - lineGap;
      const line2Y = h / 2 + fontSize + lineGap * 0.5;

      ctx.fillText(part1.trim(), w / 2, line1Y);
      ctx.fillText(part2, w / 2, line2Y);

      const imageData = ctx.getImageData(0, 0, w, h);

      // Determine part2 vertical boundary (approx line2Y - fontSize/2)
      const part2TopY = line2Y - fontSize * 0.6;

      const isMobile = w < 640;
      const step = isMobile ? 3 : Math.max(2, Math.round(w / 400));
      const newParticles: Particle[] = [];
      const newPixelData: { isPrimary: boolean }[] = [];

      let seed = 0;
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const i = (y * w + x) * 4;
          if (imageData.data[i + 3] > 128) {
            const isPrimary = y >= part2TopY;
            const angle = seededRandom(seed) * Math.PI * 2;
            const dist = 600 + seededRandom(seed + 1) * 1000;

            newParticles.push({
              ox: x, oy: y,
              ex: x + Math.cos(angle) * dist,
              ey: y + Math.sin(angle) * dist,
              radius: 1 + seededRandom(seed + 2) * 1.2,
              speed: 0.6 + seededRandom(seed + 3) * 0.8,
            });
            newPixelData.push({ isPrimary });
            seed++;
          }
        }
      }

      particlesRef.current = newParticles;
      pixelDataRef.current = newPixelData;
    } else {
      // Single-line rendering (original logic)
      const fullText = part1 + part2;

      const measureCanvas = document.createElement("canvas");
      const measureCtx = measureCanvas.getContext("2d")!;
      measureCtx.font = font;
      const fullWidth = measureCtx.measureText(fullText).width;
      const part1Width = measureCtx.measureText(part1).width;

      ctx.fillText(fullText, w / 2, h / 2);

      const imageData = ctx.getImageData(0, 0, w, h);
      const textStartX = w / 2 - fullWidth / 2;
      const part2StartX = textStartX + part1Width;

      const isMobile = w < 640;
      const step = isMobile ? 3 : Math.max(2, Math.round(w / 400));
      const newParticles: Particle[] = [];
      const newPixelData: { isPrimary: boolean }[] = [];

      let seed = 0;
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const i = (y * w + x) * 4;
          if (imageData.data[i + 3] > 128) {
            const isPrimary = x >= part2StartX;
            const angle = seededRandom(seed) * Math.PI * 2;
            const dist = 600 + seededRandom(seed + 1) * 1000;

            newParticles.push({
              ox: x, oy: y,
              ex: x + Math.cos(angle) * dist,
              ey: y + Math.sin(angle) * dist,
              radius: 1 + seededRandom(seed + 2) * 1.2,
              speed: 0.6 + seededRandom(seed + 3) * 0.8,
            });
            newPixelData.push({ isPrimary });
            seed++;
          }
        }
      }

      particlesRef.current = newParticles;
      pixelDataRef.current = newPixelData;
    }

    isInitRef.current = true;
    lastDims.current = { w, h };
  }, [textPart1, textPart2, multiLine]);

  // Rebuild on resize
  useEffect(() => {
    buildParticles(width, height);
  }, [width, height, buildParticles]);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;

    const render = () => {
      if (!isInitRef.current) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, width, height);
      const particles = particlesRef.current;
      const pixelData = pixelDataRef.current;

      // Phase mapping:
      // progress 0-0.5: assembling (0=scattered, 0.5=formed)
      // progress 0.5-1.0: exploding (0.5=formed, 1.0=exploded)

      const assembleProgress = Math.min(1, progress * 2); // 0 to 1 over progress 0-0.5
      const explodeProgress = Math.max(0, (progress - 0.5) * 2); // 0 to 1 over progress 0.5-1.0

      // Smoothstep easing
      const easeAssemble = assembleProgress * assembleProgress * (3 - 2 * assembleProgress);
      const easeExplode = explodeProgress * explodeProgress * (3 - 2 * explodeProgress);

      // Primary color (from CSS --primary)
      const primaryColor = "hsl(199, 76%, 52%)";
      const foregroundColor = "hsl(210, 20%, 95%)";

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const pd = pixelData[i];

        let x: number, y: number, alpha: number;

        if (assembleProgress >= 0.85 && explodeProgress <= 0) {
          // Fully formed — snap to exact position, no drift
          x = p.ox;
          y = p.oy;
          alpha = 1;
        } else if (explodeProgress > 0) {
          // Exploding phase: move from origin to explosion target
          const t = easeExplode * p.speed;
          x = p.ox + (p.ex - p.ox) * Math.min(1, t);
          y = p.oy + (p.ey - p.oy) * Math.min(1, t);
          alpha = Math.max(0, 1 - easeExplode * 1.3);
        } else {
          // Assembling phase
          const scatterSeed = i * 7;
          const scatterX = (seededRandom(scatterSeed) - 0.5) * width * 1.5;
          const scatterY = (seededRandom(scatterSeed + 1) - 0.5) * height * 1.5;

          x = p.ox + (scatterX) * (1 - easeAssemble);
          y = p.oy + (scatterY) * (1 - easeAssemble);
          alpha = easeAssemble;
        }

        const r = p.radius;

        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        const color = pd.isPrimary ? primaryColor : foregroundColor;

        // Parse the color and add alpha
        if (pd.isPrimary) {
          ctx.fillStyle = `hsla(199, 76%, 52%, ${alpha})`;
        } else {
          ctx.fillStyle = `hsla(210, 20%, 95%, ${alpha})`;
        }
        ctx.fill();
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [progress, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{ width, height }}
    />
  );
};

export default ParticleText;
